import React from 'react';
import { Button, Modal, ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik } from 'formik';
import { number, object } from 'yup';
import { useSelector } from 'react-redux';

import { FrontlineUserFormGroupSelect } from '@components/Feature/User';
import { ServerError } from '@components/Helper';
import { FormGroupControl } from '@components/Base';
import { yupPercentage } from '@utils/yup';
import { mergeInitialValues } from '@utils/formik';
import { Collaboration } from '@models/Collaboration';

import { CollaborationCreatePayload } from './action';
import { selectFeatureCollaboration } from './selector';

export type CollaborationFormValues = Pick<CollaborationCreatePayload, 'collaboratorId' | 'brokeragePercentage'>;

type FormFormProps = Pick<ModalProps, 'onHide'> & {
  isEdit?: boolean;
};;

const FormForm = ({ onHide, isEdit = false }: FormFormProps) => {
  const { t } = useTranslation();
  const { serverErrors } = useSelector(selectFeatureCollaboration);

  return (
    <Form>
      <Modal.Header closeButton>
        <Modal.Title>Add Collaborator</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FrontlineUserFormGroupSelect
          asRow
          label="Collaborator"
          name="collaboratorId"
          groupProps={{ className: 'mb-3' }}
          required
          disabled={isEdit}
        />

        <FormGroupControl
          asRow
          type="number"
          label="Brokerage (%)"
          name="brokeragePercentage"
          groupProps={{ className: 'mb-3' }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('confirmModal.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('common:save')}
        </Button>
      </Modal.Footer>
      <ServerError errors={serverErrors} />
    </Form>
  );
}

const defaultValues: CollaborationFormValues = {
  collaboratorId: '',
  brokeragePercentage: 0.0,
};

type Props = ModalProps & {
  onSubmit: (values: CollaborationFormValues) => void;
  collaboration?: Collaboration | null;
}

const CollaborationFormModal = ({ collaboration, onSubmit, ...props }: Props) => {
  const { t } = useTranslation();
  const isEdit = !!collaboration;
  const initialValues = mergeInitialValues(defaultValues, collaboration);

  const validationSchema = object({
    collaboratorId: number().required(t('validation:required')),
    brokeragePercentage: yupPercentage(),
  });

  return (
    <Modal {...props}>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <FormForm onHide={props.onHide} isEdit={isEdit} />
      </Formik>
    </Modal>
  )
};

export { CollaborationFormModal };
